import { GET_EHR_APPOINTMENTS } from '@/hooks/query/integrations/useQueryEHRAppointments';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { toast } from '@heidi/design-system/toast';
import { type SearchAppointmentResponse } from '@heidi/integrations/ehr/contracts';
import { useMutateUpdateSessionEHRDetails as useMutateUpdateSessionEHRDetailsCore } from '@heidi/integrations/ehr/data';
import { GET_SESSION_BY_ID, type SessionResponse } from '@heidi/session/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { get } from 'lodash-es';

export const useMutateUpdateSessionEHRDetails = (params?: {
  date?: Date;
  ehrLaunch?: boolean;
}) => {
  const queryClient = useQueryClient();
  const sessionKey = GET_SESSION_BY_ID;
  const dateInput = params?.date ? format(params?.date, 'yyyy-MM-dd') : undefined;

  return useMutateUpdateSessionEHRDetailsCore<
    Awaited<ReturnType<typeof updateSessionById>>
  >({
    updateSession: updateSessionById,
    onMutate: async ({ sessionId, appointmentId, patientId, provider, encounterId }) => {
      await queryClient.cancelQueries({
        queryKey: [sessionKey, { sessionId }],
      });

      queryClient.setQueryData<SessionResponse>(
        [GET_SESSION_BY_ID, { sessionId: sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const newData: SessionResponse = {
            ...oldData,
            ehr_appt_id:
              appointmentId !== undefined ? appointmentId : oldData.ehr_appt_id,
            ehr_patient_id: patientId !== undefined ? patientId : oldData.ehr_patient_id,
            ehr_provider: provider !== undefined ? provider : oldData.ehr_provider,
            ehr_encounter_id:
              encounterId !== undefined ? encounterId : oldData.ehr_encounter_id,
          };
          return newData;
        }
      );

      if (!dateInput) {
        return;
      }

      await queryClient.cancelQueries({
        queryKey: [GET_EHR_APPOINTMENTS, { date: dateInput }],
      });
      const previousAppointments = queryClient.getQueryData([
        GET_EHR_APPOINTMENTS,
        { date: dateInput },
      ]);
      queryClient.setQueryData<SearchAppointmentResponse>(
        [GET_EHR_APPOINTMENTS, { date: dateInput }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const newAppointments = oldData.appointments.map((appointment) => {
            if (appointment.record_id === appointmentId) {
              return {
                ...appointment,
                session_id: sessionId,
              };
            }
            return appointment;
          });

          return { appointments: newAppointments };
        }
      );

      return { previousAppointments };
    },

    onError: (error, variables) => {
      const { sessionId } = variables;

      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: [sessionKey, { sessionId }],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EHR_APPOINTMENTS, { date: dateInput }],
        }),
      ]);

      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to update session',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
};
